/**
 * Moment-like supported tokens (subset):
 * Date:  D, DD, M, MM, YY, YYYY
 * Time:  H, HH, h, hh, m, mm, s, ss
 *
 * In this mask we NORMALIZE single-letter tokens to the fixed-width variant:
 * D->DD, M->MM, H->HH, h->hh, m->mm, s->ss
 * so every token has fixed width (2 or 4).
 */

export type MaskToken = 'dd' | 'MM' | 'yy' | 'yyyy' | 'HH' | 'hh' | 'mm' | 'ss';
export type TokenType = 'day' | 'month' | 'year' | 'hour24' | 'hour12' | 'minute' | 'second';

export type TokenSeg = {
    kind: 'token';
    token: MaskToken;
    type: TokenType;
    start: number;
    end: number; // exclusive
    width: 2 | 4;
};

export type LitSeg = {
    kind: 'lit';
    value: string;
    start: number;
    end: number;
};

export type Seg = TokenSeg | LitSeg;

export const isDigit = (k: string) => /^\d$/.test(k);
export const isPlaceholder = (v: string) => /[a-zA-Z]/.test(v);

export const pad2 = (s: string) => s.padStart(2, '0');
export const pad4 = (s: string) => s.padStart(4, '0');

/** 00-68 => 2000-2068, 69-99 => 1969-1999 (default “POSIX-ish”) */
export const yyToYYYY = (yy: number, pivot = 69): number => {
    if (!Number.isFinite(yy) || yy < 0 || yy > 99) return NaN;
    return yy >= pivot ? 1900 + yy : 2000 + yy;
};

export const quantifyMonthNumbOfDays = (month: number, year: number): number => {
    month = ((month - 1 + 12) % 12) + 1;
    switch (month) {
        case 2:
            return (year % 4 === 0 && year % 100) || year % 400 === 0 ? 29 : 28;
        case 9:
        case 6:
        case 4:
        case 11:
            return 30;
        default:
            return 31;
    }
};

export const monthOrDayValidator = (day: string, month: string, year: string): boolean => {
    if (isPlaceholder(day) || isPlaceholder(month) || isPlaceholder(year)) return false;

    const d = Number(day);
    const m = Number(month);
    const y = Number(year);

    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return false;
    if (m < 1 || m > 12) return false;
    if (d < 1) return false;

    return d <= quantifyMonthNumbOfDays(m, y);
};

export function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}

export function clampOrWrap(n: number, min: number, max: number, circular: boolean): number {
    if (!Number.isFinite(n)) return min;
    if (!circular) return clamp(n, min, max);
    if (n > max) return min;
    if (n < min) return max;
    return n;
}

/** Extract moment-like tokens from an user configuration string */
export function extractMomentTokens(conf: string): string[] {
    return conf.match(/(YYYY|YY|DD|D|MM|M|HH|H|hh|h|mm|m|ss|s)/g) ?? [];
}

/** Normalize tokens to fixed width + map to internal mask tokens */
export function normalizeToMaskTokens(conf: string): MaskToken[] {
    const raw = extractMomentTokens(conf);
    return raw
        .map((t) => {
            switch (t) {
                // YEAR
                case 'YYYY':
                case 'yyyy':
                    return 'yyyy';
                case 'YY':
                case 'yy':
                    return 'yy';

                // MONTH
                case 'MM':
                    return 'MM';
                case 'M':
                    return 'MM';

                // DAY
                case 'DD':
                case 'dd':
                    return 'dd';
                case 'D':
                case 'd':
                    return 'dd';

                // HOURS
                case 'HH':
                    return 'HH';
                case 'H':
                    return 'HH';
                case 'hh':
                    return 'hh';
                case 'h':
                    return 'hh';

                // MIN/SEC
                case 'mm':
                    return 'mm';
                case 'm':
                    return 'mm';
                case 'ss':
                    return 'ss';
                case 's':
                    return 'ss';
                default:
                    return null;
            }
        })
        .filter((x): x is MaskToken => !!x);
}

export function tokenType(t: MaskToken): TokenType {
    switch (t) {
        case 'dd':
            return 'day';
        case 'MM':
            return 'month';
        case 'yy':
        case 'yyyy':
            return 'year';
        case 'HH':
            return 'hour24';
        case 'hh':
            return 'hour12';
        case 'mm':
            return 'minute';
        case 'ss':
            return 'second';
    }
}

export function tokenWidth(t: MaskToken): 2 | 4 {
    return t === 'yyyy' ? 4 : 2;
}

export function buildSegmentsFromMaskViewModel(viewModel: string): Seg[] {
    const segs: Seg[] = [];
    const tokens: MaskToken[] = ['yyyy', 'dd', 'MM', 'HH', 'hh', 'mm', 'ss', 'yy'];
    let i = 0;

    while (i < viewModel.length) {
        // try long tokens first
        let matched: MaskToken | null = null;

        for (const tk of tokens) {
            const len = tk.length;
            if (i + len <= viewModel.length && viewModel.slice(i, i + len) === tk) {
                matched = tk;
                break;
            }
        }

        if (matched) {
            const len = matched.length;
            segs.push({
                kind: 'token',
                token: matched,
                type: tokenType(matched),
                start: i,
                end: i + len,
                width: tokenWidth(matched),
            });
            i += len;
            continue;
        }

        // literal run until next token
        let j = i + 1;
        outer: while (j <= viewModel.length) {
            for (const tk of tokens) {
                const len = tk.length;
                if (j + len <= viewModel.length && viewModel.slice(j, j + len) === tk) break outer;
            }
            j++;
        }

        segs.push({ kind: 'lit', value: viewModel.slice(i, j), start: i, end: j });
        i = j;
    }

    return segs;
}
